class StickyFooter {
  constructor() {
    this.srcElement = document.querySelector('[data-sticky-src]');
    this.footerElement = document.querySelector('[data-sticky-footer]');
    this.isVisible = false;
    this.ticking = false;
    if (!this.srcElement || !this.footerElement) return;
    this.init();
  }
  isSrcOutOfView() {
    return this.srcElement.getBoundingClientRect().bottom < 0;
  }
  show() {
    if (this.isVisible) return;
    this.footerElement.classList.add('sticky-footer--active');
    document.documentElement.classList.add('sticky-footer-visible'); // chat launchers lift above the bar (custom.css, L3-8)
    this.isVisible = true;
  }
  hide() {
    if (!this.isVisible) return;
    this.footerElement.classList.remove('sticky-footer--active');
    document.documentElement.classList.remove('sticky-footer-visible');
    this.isVisible = false;
  }
  handleScroll() {
    this.isSrcOutOfView() ? this.show() : this.hide();
  }
  onScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.handleScroll();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }
  init() {
    this.handleScroll();
    requestAnimationFrame(() => {
      this.footerElement.classList.add('sticky-footer--init');
    });
    window.addEventListener('scroll', () => this.onScroll(), { passive: true });
  }
}
document.readyState === 'loading' 
  ? document.addEventListener('DOMContentLoaded', () => new StickyFooter())
  : new StickyFooter();

class ElementSync {
  constructor(primary, secondary) {
    this.primary = primary;
    this.secondary = secondary;
    this.init();
  }

  init() {
    const isButton = this.primary.tagName === 'BUTTON' || this.primary.type === 'button';
    
    if (isButton) {
      this.secondary.addEventListener('click', () => this.primary.click());
    } else {
      this.secondary.addEventListener('input', (e) => {
        this.primary.value = e.target.value;
        this.primary.dispatchEvent(new Event('input', { bubbles: true }));
      });
      
      this.primary.addEventListener('input', (e) => {
        this.secondary.value = e.target.value;
      });
    }
  }
}

/**
 * ReadMore - A reusable class for implementing expandable text with line-based truncation
 * @class
 * @example
 * const readMore = new ReadMore('.product-description_content', {
 *   maxLines: 3,
 *   readMoreText: 'Read more',
 *   readLessText: 'Read less'
 * });
 */
class ReadMore {
  /**
   * @param {string|HTMLElement} element - Target element selector or DOM element
   * @param {Object} options - Configuration options
   * @param {number} [options.maxLines=3] - Maximum number of lines to show when collapsed
   * @param {string} [options.readMoreText='Read more'] - Text for expand button
   * @param {string} [options.readLessText='Read less'] - Text for collapse button
   * @param {boolean} [options.scrollOnToggle=true] - Scroll the element into view on expand/collapse
   * @param {number} [options.scrollOffset=20] - Additional offset for smooth scrolling (px)
   * @param {string} [options.buttonClass='read-more-btn'] - CSS class for the toggle button
   */
  constructor(element, options = {}) {
    this.element = typeof element === 'string' 
      ? document.querySelector(element) 
      : element;

    if (!this.element) {
      console.warn('ReadMore: Element not found');
      return;
    }

    this.options = {
      maxLines: 3,
      readMoreText: 'Read more',
      readLessText: 'Read less',
      scrollOnToggle: true,
      scrollOffset: 20,
      buttonClass: 'read-more-btn',
      ...options
    };

    this.isExpanded = false;
    this.originalHeight = null;
    this.collapsedHeight = null;
    this.button = null;

    this.init();
  }

  /**
   * Initialize the ReadMore functionality
   * @private
   */
  init() {
    // Add data attribute and set line clamp value
    this.element.setAttribute('data-readmore', 'collapsed');
    this.element.setAttribute('data-readmore-lines', this.options.maxLines);
    
    // Store original content
    this.originalContent = this.element.innerHTML;
    
    // Wait for next frame to get accurate scrollHeight after CSS is applied
    requestAnimationFrame(() => {
      this.originalHeight = this.element.scrollHeight;

      // Calculate line height and collapsed height
      const computedStyle = window.getComputedStyle(this.element);
      const lineHeight = parseFloat(computedStyle.lineHeight) || 
                         parseFloat(computedStyle.fontSize) * 1.2;
      
      this.collapsedHeight = lineHeight * this.options.maxLines;

      // Check if truncation is needed
      if (this.originalHeight <= this.collapsedHeight) {
        // Content fits within max lines, remove data attribute
        this.element.removeAttribute('data-readmore');
        this.element.removeAttribute('data-readmore-lines');
        return;
      }

      // Create toggle button
      this.createButton();
    });
  }

  /**
   * Create and insert toggle button
   * @private
   */
  createButton() {
    this.button = document.createElement('button');
    this.button.className = this.options.buttonClass;
    this.button.textContent = this.options.readMoreText;
    this.button.type = 'button';
    this.button.setAttribute('aria-expanded', 'false');
    
    // Add default styles if no custom class styling exists
    if (!this.hasCustomStyles()) {
      this.applyDefaultButtonStyles();
    }

    this.button.addEventListener('click', () => this.toggle());
    
    // Insert button after the element
    this.element.parentNode.insertBefore(
      this.button, 
      this.element.nextSibling
    );
  }

  /**
   * Check if custom button styles are defined
   * @private
   * @returns {boolean}
   */
  hasCustomStyles() {
    const testEl = document.createElement('div');
    testEl.className = this.options.buttonClass;
    document.body.appendChild(testEl);
    const hasStyles = window.getComputedStyle(testEl).marginTop !== '0px' ||
                      window.getComputedStyle(testEl).padding !== '0px';
    document.body.removeChild(testEl);
    return hasStyles;
  }

  /**
   * Apply default button styles
   * @private
   */
  applyDefaultButtonStyles() {
    Object.assign(this.button.style, {
      marginTop: '12px',
      padding:'0px',
      background: 'transparent',
      border: 'none',
      color: 'var(--accent-text-color, #0066cc)',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      textDecoration: 'underline',
      transition: 'opacity 0.2s ease'
    });

    this.button.addEventListener('mouseenter', () => {
      this.button.style.opacity = '0.7';
    });

    this.button.addEventListener('mouseleave', () => {
      this.button.style.opacity = '1';
    });
  }

  /**
   * Toggle expand/collapse state
   * @public
   */
  toggle() {
    if (this.isExpanded) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  /**
   * Expand the content
   * @public
   */
  expand() {
    this.isExpanded = true;

    // Update data attribute
    this.element.setAttribute('data-readmore', 'expanded');
    
    // Set explicit height for smooth transition
    this.element.style.maxHeight = `${this.element.scrollHeight}px`;

    // Update button
    this.button.textContent = this.options.readLessText;
    this.button.setAttribute('aria-expanded', 'true');

    // Scroll to element after expansion animation
    if (this.options.scrollOnToggle) {
      setTimeout(() => {
        this.scrollToElement();
      }, 300);
    }
  }

  /**
   * Collapse the content
   * @public
   */
  collapse() {
    this.isExpanded = false;

    // Get element position before collapse for smooth scrolling
    const elementTop = this.element.getBoundingClientRect().top + window.pageYOffset;

    // Update data attribute
    this.element.setAttribute('data-readmore', 'collapsed');

    // Remove inline max-height to let CSS take over
    this.element.style.maxHeight = '';

    // Update button
    this.button.textContent = this.options.readMoreText;
    this.button.setAttribute('aria-expanded', 'false');

    // Scroll to top of element
    if (this.options.scrollOnToggle) {
      window.scrollTo({
        top: elementTop - this.options.scrollOffset,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Scroll to element position
   * @private
   */
  scrollToElement() {
    const elementTop = this.element.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({
      top: elementTop - this.options.scrollOffset,
      behavior: 'smooth'
    });
  }

  /**
   * Destroy the instance and restore original state
   * @public
   */
  destroy() {
    if (this.button) {
      this.button.remove();
    }

    // Remove data attributes
    this.element.removeAttribute('data-readmore');
    this.element.removeAttribute('data-readmore-lines');
    
    // Restore original styles
    this.element.style.maxHeight = '';
  }

  /**
   * Re-initialize with new options
   * @public
   * @param {Object} newOptions - New configuration options
   */
  reinit(newOptions = {}) {
    this.destroy();
    this.options = { ...this.options, ...newOptions };
    this.isExpanded = false;
    this.init();
  }
}


class Accordion {
    constructor(selector, config = {}) {
        this.selector = selector;
        this.config = {
            openClass: 'is-open',
            duration: 300,
            openMultiple: true,
            startOpen: true,
            ...config
        };
        
        this.items = [];
        this.init();
    }

    init() {
        const elements = document.querySelectorAll(`[data-accordion="${this.selector}"]`);

        if(elements.length < 1) return;
        const configOpen = elements[0].closest('[data-accordion-open]');

        elements.forEach(el => {
            if (el.dataset.accordionInit) return;

            const trigger = el.querySelector('[data-accordion-trigger]');
            const content = el.querySelector('[data-accordion-content]');
            
            if (!trigger || !content) return;
            
            const shouldBeOpen = this.config.startOpen || el.classList.contains(this.config.openClass);
            
            if (shouldBeOpen) {
                el.classList.add(this.config.openClass);
                content.style.maxHeight = 'none';
                requestAnimationFrame(() => {
                    content.style.maxHeight = (content.scrollHeight + 5) + 'px';
                });
            } else {
                el.classList.remove(this.config.openClass);
                content.style.maxHeight = '0';
            }
            
            const handler = () => this.toggle(el);
            trigger.addEventListener('click', handler);

           

            el.dataset.accordionInit = 'true';
            
            el._accordionHandler = handler;
            this.items.push({ el, trigger, content });
        });
        configOpen?.removeAttribute('data-accordion-open');
    }

    toggle(item) {
        const isOpen = item.classList.contains(this.config.openClass);
        const content = item.querySelector('[data-accordion-content]');
        
        if (!this.config.openMultiple && !isOpen) {
            this.closeAll(item);
        }
        
        if (isOpen) {
            item.classList.remove(this.config.openClass);
            content.style.maxHeight = '0';
        } else {
            item.classList.add(this.config.openClass);
            content.style.maxHeight = (content.scrollHeight + 5) + 'px';
        }
    }

    closeAll(except = null) {
        this.items.forEach(({ el, content }) => {
            if (el !== except && el.classList.contains(this.config.openClass)) {
                el.classList.remove(this.config.openClass);
                content.style.maxHeight = '0';
            }
        });
    }

    static reinitAll() {
        const selectors = new Set();
        document.querySelectorAll('[data-accordion]').forEach(el => {
            selectors.add(el.dataset.accordion);
        });
        
        selectors.forEach(selector => {
            new Accordion(selector);
        });
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => Accordion.reinitAll());
} else {
    Accordion.reinitAll();
}

window.Accordion = Accordion;

document.addEventListener('modal_opened', () => {
    document.querySelectorAll('.modal-active [data-accordion-init]').forEach(el => {
        delete el.dataset.accordionInit;
    });
    Accordion.reinitAll();
});


// class InputSync {
//   constructor() {
//     this.syncGroups = {};
//     this.syncing = false;
//   }

//   init() {
//     this.resetSyncGroups();
//     document.querySelectorAll('[data-input-sync]').forEach((element) => {
//       this.addToGroupFromElement(element);
//     });
//   }

//   resetSyncGroups() {
//     this.syncGroups = {}; // Reset the sync groups to avoid duplicates
//   }

//   addToGroupFromElement(element) {
//     const syncKey = element.getAttribute('data-input-sync');
//     if (!this.syncGroups[syncKey]) {
//       this.syncGroups[syncKey] = [];
//     }

//     this.syncGroups[syncKey].push(element);

//     if (element.type === 'radio') {
//       element.addEventListener('change', (e) => {
//         if (!this.syncing && e.target.checked) {
//           this.syncRadio(syncKey, e.target);
//         }
//       });
//     } else {
//       element.addEventListener('input', (e) => {
//         this.syncValue(syncKey, e.target);
//       });
//     }
//   }

//   syncValue(syncKey, sourceElement) {
//     const value = sourceElement.value;
//     this.syncGroups[syncKey].forEach((el) => {
//       if (el !== sourceElement && el.type !== 'radio') {
//         el.value = value;
//       }
//     });
//   }

//   syncRadio(syncKey, sourceElement) {
//     this.syncing = true;
//     const value = sourceElement.value;
//     const name = sourceElement.name;
//     this.syncGroups[syncKey].forEach((el) => {
//       if (el !== sourceElement && el.type === 'radio' && el.name === name && el.value === value) {
//         el.checked = true;
//       }
//     });
//     this.syncing = false;
//   }

//   getSyncGroup(syncKey) {
//     return this.syncGroups[syncKey] || [];
//   }

//   addToGroup(syncKey, element) {
//     if (!this.syncGroups[syncKey]) {
//       this.syncGroups[syncKey] = [];
//     }

//     if (!this.syncGroups[syncKey].includes(element)) {
//       this.syncGroups[syncKey].push(element);

//       if (element.type === 'radio') {
//         element.addEventListener('change', (e) => {
//           if (!this.syncing && e.target.checked) {
//             this.syncRadio(syncKey, e.target);
//           }
//         });
//       } else {
//         element.addEventListener('input', (e) => {
//           this.syncValue(syncKey, e.target);
//         });
//       }
//     }
//   }

//   removeFromGroup(syncKey, element) {
//     if (this.syncGroups[syncKey]) {
//       this.syncGroups[syncKey] = this.syncGroups[syncKey].filter(el => el !== element);
//     }
//   }

//   reinit() {
//     this.init();
//   }
// }

// if (!window.inputSyncInstance) {
//   window.inputSyncInstance = new InputSync();
//   document.addEventListener('modal_opened', () => {
//     window.inputSyncInstance.reinit();
//   });
// }